import { useState, useEffect, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./memecandy.css";
import { useAudio } from "../../../Providers/AudioProvider";
import task from "../Task";
import useUserInfo from "../../../Hooks/useUserInfo";
import { userGameRewards } from "../../../apis/user";
import { logPageView } from "../../../components/analytics";
import clickSound from "./click.mp3";
import {
  vibrate,
  vibrateSuccess,
  vibrateError,
} from "../../../utils/vibration";

export default function MemeCandyGame() {
  const GRID_SIZE = 8;
  // Replace emoji candies with custom image URLs
  const CANDY_IMAGES = [
    "https://d1ccopf5cw1hft.cloudfront.net/s3data/dodge.png",
    "https://d1ccopf5cw1hft.cloudfront.net/s3data/pepe.png",
    "https://d1ccopf5cw1hft.cloudfront.net/s3data/wif.png",
    "https://d1ccopf5cw1hft.cloudfront.net/s3data/bonk.png",
    "https://d1ccopf5cw1hft.cloudfront.net/s3data/shiba.png",
    "https://d1ccopf5cw1hft.cloudfront.net/s3data/ton.png",
    "https://d1ccopf5cw1hft.cloudfront.net/s3data/trump.png",
  ];

  const [board, setBoard] = useState([]);
  const [selectedCandy, setSelectedCandy] = useState(null);
  const [score, setScore] = useState(0);
  const [moves, setMoves] = useState(25);
  const [gameOver, setGameOver] = useState(false);
  const [IsGameOver, setIsGameOver] = useState(false);
  const [message, setMessage] = useState("");
  const [isSwapping, setIsSwapping] = useState(false);
  const [floatingPoints, setFloatingPoints] = useState([]);
  const [touchStart, setTouchStart] = useState(null);
  const [gameActive, setGameActive] = useState(false);
  const [swapDirection, setSwapDirection] = useState(null);
  const [animatingCells, setAnimatingCells] = useState([]);
  const boardRef = useRef(null);
  const matchSoundRef = useRef(new Audio(clickSound));
  const [initialMatchCount, setInitialMatchCount] = useState(0);
  const [hintPositions, setHintPositions] = useState([]);
  const [showingHint, setShowingHint] = useState(false);
  const inactivityTimerRef = useRef(null);
  const lastActionTimeRef = useRef(Date.now());
  const [showInstructionsModal, setShowInstructionsModal] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);

  const { userDetails, updateUserInfo, updatewatchScreenInfo, watchScreen } =
    useUserInfo();
  const playClick = useAudio();

  const closePopUp = () => {
    playClick();
    // Reset all game states to prevent popup from showing again
    setIsGameOver(false);
    setGameOver(false);
    // Ensure we're ready for a new game
    setGameActive(false);
    setScore(0);
  };

  const IsPointsReached =
    watchScreen?.LoginMessage ===
    "You have already reached the maximum rewards limit"
      ? true
      : false;

  const toogleMenu = () => {
    playClick();
    updateUserInfo((prev) => ({
      ...prev,
      isPlay: false,
      currentComponent: task,
      currentComponentText: "task",
      lastComponent: userDetails?.userDetails.currentComponent,
      lastComponentText: userDetails?.userDetails.currentComponentText,
      isMenu: true,
      menuCount: userDetails?.userDetails?.menuCount + 1,
    }));
  };

  const handleApiResponse = async (points) => {
    updateUserInfo((prev) => ({
      ...prev,
      isLoading: true,
    }));
    try {
      const apiData = {
        gamePoints: points,
        gameType: "memecandy",
      };
      const res = await userGameRewards(apiData);
      if (res) {
        updateUserInfo((prev) => ({
          ...prev,
          isLoading: false,
        }));
        updatewatchScreenInfo((prev) => ({
          ...prev,
          LoginMessage: res?.message,
        }));
        setIsGameOver(true);
      }
    } catch (error) {
      console.error("API call failed:", error);
      updateUserInfo((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  };

  // Preload candy images
  useEffect(() => {
    // Preload all candy images
    CANDY_IMAGES.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  // Add meta viewport tag for mobile devices
  useEffect(() => {
    // Add meta viewport tag to prevent zooming
    const viewportMeta = document.createElement("meta");
    viewportMeta.name = "viewport";
    viewportMeta.content =
      "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no";
    document.head.appendChild(viewportMeta);

    // Prevent iOS-specific gestures (especially important for Telegram mini app)
    const preventDefaultForTouch = (e) => {
      // Don't prevent default for buttons and interactive elements
      if (
        e.target.tagName === "BUTTON" ||
        e.target.closest("button") ||
        e.target.role === "button" ||
        e.target.className.includes("start-btn") ||
        e.target.className.includes("control-btn") ||
        e.target.onclick
      ) {
        return; // Allow buttons to work
      }

      e.preventDefault();
      e.stopPropagation();
    };

    // Add event listeners to prevent iOS gestures on the entire document
    document.addEventListener("touchstart", preventDefaultForTouch, {
      passive: false,
    });
    document.addEventListener("touchmove", preventDefaultForTouch, {
      passive: false,
    });
    document.addEventListener("touchend", preventDefaultForTouch, {
      passive: false,
    });

    // Add specific iOS body styling to prevent bouncing/scrolling
    // Using a less restrictive approach that still allows buttons to work
    document.body.style.overscrollBehavior = "none";
    document.body.style.overflow = "hidden";

    // Clean up when component unmounts
    return () => {
      document.head.removeChild(viewportMeta);
      document.removeEventListener("touchstart", preventDefaultForTouch);
      document.removeEventListener("touchmove", preventDefaultForTouch);
      document.removeEventListener("touchend", preventDefaultForTouch);
      document.body.style.overscrollBehavior = "";
      document.body.style.overflow = "";
    };
  }, []);

  // Initialize board when component mounts
  useEffect(() => {
    initializeBoard();
  }, []);

  // Check for matches after each move
  useEffect(() => {
    if (board.length > 0 && gameActive) {
      checkMatches();
    }
  }, [board, gameActive]);

  // Add a new effect to check for cascading matches
  useEffect(() => {
    if (board.length > 0 && gameActive) {
      // Use a slight delay to allow animations to complete
      const timer = setTimeout(() => {
        // Check if there are any ongoing matches after pieces have fallen
        const hasMatches = checkForPotentialMatches();
        if (hasMatches) {
          console.log("Cascading matches found");
          // This will trigger the checkMatches effect above
          setBoard([...board]);
        }
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [floatingPoints]);

  // Function to check if there are any potential matches on the board
  const checkForPotentialMatches = () => {
    // Clone the board for checking
    const testBoard = JSON.parse(JSON.stringify(board));

    // Check horizontal matches
    for (let i = 0; i < GRID_SIZE; i++) {
      for (let j = 0; j < GRID_SIZE - 2; j++) {
        if (
          testBoard[i][j] !== "" &&
          testBoard[i][j] === testBoard[i][j + 1] &&
          testBoard[i][j] === testBoard[i][j + 2]
        ) {
          return true;
        }
      }
    }

    // Check vertical matches
    for (let j = 0; j < GRID_SIZE; j++) {
      for (let i = 0; i < GRID_SIZE - 2; i++) {
        if (
          testBoard[i][j] !== "" &&
          testBoard[i][j] === testBoard[i + 1][j] &&
          testBoard[i][j] === testBoard[i + 2][j]
        ) {
          return true;
        }
      }
    }

    return false;
  };

  // Check if game is over
  useEffect(() => {
    if (moves <= 0 && !gameOver) {
      setGameActive(false);
      setGameOver(true);
      handleApiResponse(score);
    }
  }, [moves]);

  // Remove floating points after animation
  useEffect(() => {
    if (floatingPoints.length > 0) {
      const timer = setTimeout(() => {
        setFloatingPoints([]);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [floatingPoints]);

  const countPossibleMatches = (board) => {
    let possibleMatches = 0;
    const checkedSwaps = new Set(); // Track checked swaps to avoid duplicates

    // Check all possible horizontal swaps
    for (let i = 0; i < GRID_SIZE; i++) {
      for (let j = 0; j < GRID_SIZE - 1; j++) {
        // Create a unique key for this swap
        const swapKey = `${i},${j}-${i},${j + 1}`;
        if (checkedSwaps.has(swapKey)) continue;
        checkedSwaps.add(swapKey);

        // Try swapping with the right candy
        let tempBoard = JSON.parse(JSON.stringify(board));
        const temp = tempBoard[i][j];
        tempBoard[i][j] = tempBoard[i][j + 1];
        tempBoard[i][j + 1] = temp;

        // Check if this swap creates a match
        if (hasMatch(tempBoard, i, j) || hasMatch(tempBoard, i, j + 1)) {
          possibleMatches++;
        }
      }
    }

    // Check all possible vertical swaps
    for (let i = 0; i < GRID_SIZE - 1; i++) {
      for (let j = 0; j < GRID_SIZE; j++) {
        // Create a unique key for this swap
        const swapKey = `${i},${j}-${i + 1},${j}`;
        if (checkedSwaps.has(swapKey)) continue;
        checkedSwaps.add(swapKey);

        // Try swapping with the candy below
        let tempBoard = JSON.parse(JSON.stringify(board));
        const temp = tempBoard[i][j];
        tempBoard[i][j] = tempBoard[i + 1][j];
        tempBoard[i + 1][j] = temp;

        // Check if this swap creates a match
        if (hasMatch(tempBoard, i, j) || hasMatch(tempBoard, i + 1, j)) {
          possibleMatches++;
        }
      }
    }

    return possibleMatches;
  };

  // Check if there's a match at the given position
  const hasMatch = (board, row, col) => {
    const candy = board[row][col];
    if (!candy) return false;

    // Check horizontal match
    let horizontalCount = 1;
    // Check left
    for (let j = col - 1; j >= 0 && board[row][j] === candy; j--) {
      horizontalCount++;
    }
    // Check right
    for (let j = col + 1; j < GRID_SIZE && board[row][j] === candy; j++) {
      horizontalCount++;
    }

    // Check vertical match
    let verticalCount = 1;
    // Check up
    for (let i = row - 1; i >= 0 && board[i][col] === candy; i--) {
      verticalCount++;
    }
    // Check down
    for (let i = row + 1; i < GRID_SIZE && board[i][col] === candy; i++) {
      verticalCount++;
    }

    return horizontalCount >= 3 || verticalCount >= 3;
  };

  // Find hints for the player
  const findHint = () => {
    if (!gameActive || gameOver) return;

    // Clear any existing hints
    setHintPositions([]);

    // Check all possible moves to find matches
    const possibleHints = [];

    // Check horizontal swaps for potential matches
    for (let i = 0; i < GRID_SIZE; i++) {
      for (let j = 0; j < GRID_SIZE - 1; j++) {
        // Try swapping with the right candy
        let tempBoard = JSON.parse(JSON.stringify(board));
        const temp = tempBoard[i][j];
        tempBoard[i][j] = tempBoard[i][j + 1];
        tempBoard[i][j + 1] = temp;

        // Check if this swap creates a match and what length
        const matchInfo = getMatchLength(tempBoard, i, j);
        if (matchInfo.hasMatch) {
          possibleHints.push({
            positions: [
              { row: i, col: j },
              { row: i, col: j + 1 },
            ],
            matchLength: matchInfo.matchLength,
          });
        }

        // Also check the other position
        const matchInfo2 = getMatchLength(tempBoard, i, j + 1);
        if (matchInfo2.hasMatch && !matchInfo.hasMatch) {
          possibleHints.push({
            positions: [
              { row: i, col: j },
              { row: i, col: j + 1 },
            ],
            matchLength: matchInfo2.matchLength,
          });
        }
      }
    }

    // Check vertical swaps for potential matches
    for (let i = 0; i < GRID_SIZE - 1; i++) {
      for (let j = 0; j < GRID_SIZE; j++) {
        // Try swapping with the candy below
        let tempBoard = JSON.parse(JSON.stringify(board));
        const temp = tempBoard[i][j];
        tempBoard[i][j] = tempBoard[i + 1][j];
        tempBoard[i + 1][j] = temp;

        // Check if this swap creates a match and what length
        const matchInfo = getMatchLength(tempBoard, i, j);
        if (matchInfo.hasMatch) {
          possibleHints.push({
            positions: [
              { row: i, col: j },
              { row: i + 1, col: j },
            ],
            matchLength: matchInfo.matchLength,
          });
        }

        // Also check the other position
        const matchInfo2 = getMatchLength(tempBoard, i + 1, j);
        if (matchInfo2.hasMatch && !matchInfo.hasMatch) {
          possibleHints.push({
            positions: [
              { row: i, col: j },
              { row: i + 1, col: j },
            ],
            matchLength: matchInfo2.matchLength,
          });
        }
      }
    }

    // Sort hints by match length (favor longer matches)
    possibleHints.sort((a, b) => b.matchLength - a.matchLength);

    // Select a hint to show
    if (possibleHints.length > 0) {
      // Prioritize longer matches (4+ or 5+)
      const bestHints = possibleHints.filter((hint) => hint.matchLength >= 4);

      if (bestHints.length > 0) {
        // Pick a random hint from the best options
        const selectedHint =
          bestHints[Math.floor(Math.random() * bestHints.length)];
        setHintPositions(selectedHint.positions);
      } else {
        // Fall back to any hint if no 4+ matches
        const selectedHint =
          possibleHints[Math.floor(Math.random() * possibleHints.length)];
        setHintPositions(selectedHint.positions);
      }

      setShowingHint(true);

      // Auto-hide hint after 3 seconds
      setTimeout(() => {
        setHintPositions([]);
        setShowingHint(false);
      }, 3000);

      return true;
    }

    return false;
  };

  // Get match length information for a position
  const getMatchLength = (board, row, col) => {
    const candy = board[row][col];
    if (!candy) return { hasMatch: false, matchLength: 0 };

    // Check horizontal match
    let horizontalCount = 1;
    // Check left
    for (let j = col - 1; j >= 0 && board[row][j] === candy; j--) {
      horizontalCount++;
    }
    // Check right
    for (let j = col + 1; j < GRID_SIZE && board[row][j] === candy; j++) {
      horizontalCount++;
    }

    // Check vertical match
    let verticalCount = 1;
    // Check up
    for (let i = row - 1; i >= 0 && board[i][col] === candy; i--) {
      verticalCount++;
    }
    // Check down
    for (let i = row + 1; i < GRID_SIZE && board[i][col] === candy; i++) {
      verticalCount++;
    }

    const maxLength = Math.max(horizontalCount, verticalCount);
    return {
      hasMatch: maxLength >= 3,
      matchLength: maxLength,
    };
  };

  const initializeBoard = () => {
    const newBoard = [];
    for (let i = 0; i < GRID_SIZE; i++) {
      const row = [];
      for (let j = 0; j < GRID_SIZE; j++) {
        row.push(CANDY_IMAGES[Math.floor(Math.random() * CANDY_IMAGES.length)]);
      }
      newBoard.push(row);
    }

    // Count possible matches on the initial board
    const matches = countPossibleMatches(newBoard);
    setInitialMatchCount(matches);

    setBoard(newBoard);
  };

  // Get candy position from screen coordinates
  const getCandyPositionFromCoords = (x, y) => {
    if (!boardRef.current) return null;

    const boardRect = boardRef.current.getBoundingClientRect();

    // Calculate relative position within the board
    const relX = x - boardRect.left;
    const relY = y - boardRect.top;

    // Calculate cell size (accounting for gaps)
    const cellWidth = boardRect.width / GRID_SIZE;
    const cellHeight = boardRect.height / GRID_SIZE;

    // Calculate row and column
    const col = Math.floor(relX / cellWidth);
    const row = Math.floor(relY / cellHeight);

    // Check if within bounds
    if (row >= 0 && row < GRID_SIZE && col >= 0 && col < GRID_SIZE) {
      return { row, col };
    }

    return null;
  };

  // Auto-detect inactivity and show hints
  useEffect(() => {
    if (!gameActive || gameOver) return;

    // Reset inactivity timer on component mount
    lastActionTimeRef.current = Date.now();

    // Set up inactivity timer
    const checkInactivity = () => {
      const currentTime = Date.now();
      const inactiveTime = currentTime - lastActionTimeRef.current;

      // If player has been inactive for 5 seconds and no hint is currently showing
      if (inactiveTime > 5000 && !showingHint && gameActive && !isSwapping) {
        findHint();
      }
    };

    // Check every 1 second
    inactivityTimerRef.current = setInterval(checkInactivity, 1000);

    return () => {
      if (inactivityTimerRef.current) {
        clearInterval(inactivityTimerRef.current);
      }
    };
  }, [gameActive, gameOver, showingHint, isSwapping]);

  // Update last action time whenever the player makes a move
  const updateLastActionTime = () => {
    lastActionTimeRef.current = Date.now();

    // Clear any showing hints
    if (showingHint) {
      setHintPositions([]);
      setShowingHint(false);
    }
  };

  // Handle touch start event
  const handleTouchStart = (e) => {
    if (gameOver || isSwapping) return;

    // Prevent default browser behavior that might cause zooming
    e.preventDefault();

    const touch = e.touches[0];
    const position = getCandyPositionFromCoords(touch.clientX, touch.clientY);

    if (position) {
      setTouchStart(position);
      setSelectedCandy(position);
      if (!gameStarted) {
        setGameStarted(true);
      }
      updateLastActionTime();
    }
  };

  // Handle touch end event
  const handleTouchEnd = (e) => {
    if (gameOver || isSwapping || !touchStart) return;

    // Prevent default browser behavior
    e.preventDefault();

    setSelectedCandy(null);
    setTouchStart(null);
  };

  // Handle touch move event for swipe
  const handleTouchMove = (e) => {
    if (gameOver || isSwapping || !touchStart) return;

    // Prevent default browser behavior including page scrolling and zooming
    e.preventDefault();
    e.stopPropagation();

    const touch = e.touches[0];
    const currentPosition = getCandyPositionFromCoords(
      touch.clientX,
      touch.clientY
    );

    if (!currentPosition) return;

    // If we've moved to a different candy
    if (
      currentPosition.row !== touchStart.row ||
      currentPosition.col !== touchStart.col
    ) {
      // Check if adjacent
      const isAdjacent =
        (Math.abs(touchStart.row - currentPosition.row) === 1 &&
          touchStart.col === currentPosition.col) ||
        (Math.abs(touchStart.col - currentPosition.col) === 1 &&
          touchStart.row === currentPosition.row);

      if (isAdjacent) {
        swapCandies(
          touchStart.row,
          touchStart.col,
          currentPosition.row,
          currentPosition.col
        );
        setSelectedCandy(null);
        setTouchStart(null);
        updateLastActionTime();
      }
    }
  };

  const handleCandyClick = (row, col) => {
    if (gameOver || isSwapping) return;

    if (!gameStarted) {
      setGameStarted(true);
    }

    if (selectedCandy === null) {
      setSelectedCandy({ row, col });
      updateLastActionTime();
    } else {
      // Check if adjacent
      const isAdjacent =
        (Math.abs(selectedCandy.row - row) === 1 &&
          selectedCandy.col === col) ||
        (Math.abs(selectedCandy.col - col) === 1 && selectedCandy.row === row);

      if (isAdjacent) {
        swapCandies(selectedCandy.row, selectedCandy.col, row, col);
        updateLastActionTime();
      } else if (selectedCandy.row === row && selectedCandy.col === col) {
        // They clicked the same candy again, deselect it
        setSelectedCandy(null);
        updateLastActionTime();
      } else {
        setMessage("Candies must be adjacent!");
        setTimeout(() => setMessage(""), 1000);
        // Select the new candy instead
        setSelectedCandy({ row, col });
        updateLastActionTime();
      }
    }
  };

  const swapCandies = (row1, col1, row2, col2) => {
    setIsSwapping(true);
    // Reset inactivity timer since user just made a move
    updateLastActionTime();

    // Determine swap direction
    let direction;
    if (row1 === row2) {
      direction = col1 < col2 ? "right" : "left";
    } else {
      direction = row1 < row2 ? "down" : "up";
    }
    setSwapDirection(direction);
    // Set animating cells for visual feedback
    setAnimatingCells([
      { row: row1, col: col1 },
      { row: row2, col: col2 },
    ]);
    // Wait for animation to complete before updating the board
    setTimeout(() => {
      const newBoard = [...board];
      const temp = newBoard[row1][col1];
      newBoard[row1][col1] = newBoard[row2][col2];
      newBoard[row2][col2] = temp;
      setBoard(newBoard);
      setMoves(moves - 1);

      // Clear animation state
      setAnimatingCells([]);
      setSwapDirection(null);
      setIsSwapping(false);
    }, 400); // Match the animation duration
  };

  const addFloatingPoints = (row, col, points) => {
    const id = Date.now();
    setFloatingPoints((prev) => [...prev, { id, row, col, points }]);
  };

  // Function to vibrate device when candies match
  const vibrateDevice = () => {
    try {
      // Use the cross-platform vibration utility
      vibrate(80);
    } catch (error) {
      console.error("Vibration error:", error);
      // Fallback to navigator.vibrate
      if (navigator.vibrate) {
        navigator.vibrate([100, 50, 150]);
      }
    }
  };

  // Function to play match sound
  const playMatchSound = () => {
    matchSoundRef.current.currentTime = 0;
    matchSoundRef.current
      .play()
      .catch((err) => console.log("Audio play error:", err));
  };

  const checkMatches = () => {
    let matchFound = false;
    const newBoard = [...board];
    const matchedPositions = new Set();

    // Check horizontal matches
    for (let i = 0; i < GRID_SIZE; i++) {
      for (let j = 0; j < GRID_SIZE - 2; j++) {
        if (matchedPositions.has(`${i},${j}`)) continue;

        if (
          newBoard[i][j] !== "" &&
          newBoard[i][j] === newBoard[i][j + 1] &&
          newBoard[i][j] === newBoard[i][j + 2]
        ) {
          let matchLength = 3;
          while (
            j + matchLength < GRID_SIZE &&
            newBoard[i][j] === newBoard[i][j + matchLength]
          ) {
            matchLength++;
          }

          // Only award points if the game has started
          if (gameStarted) {
            // Calculate points based on match length with bonuses
            let points = matchLength * 10;

            // Add bonus points for longer matches
            if (matchLength === 4) {
              points += 20; // Bonus for 4 in a row
            } else if (matchLength >= 5) {
              points += 50; // Bonus for 5+ in a row
            }

            // Add floating points at the middle of the match
            const middleCol = j + Math.floor(matchLength / 2);
            addFloatingPoints(i, middleCol, points);

            // Immediately vibrate on match found before clearing
            vibrateDevice();
            playMatchSound();

            setScore((prev) => prev + points);
          }

          // Clear matched candies and track their positions
          for (let k = 0; k < matchLength; k++) {
            matchedPositions.add(`${i},${j + k}`);
            newBoard[i][j + k] = "";
          }

          matchFound = true;
          j += matchLength - 1;
        }
      }
    }

    // Check vertical matches
    for (let j = 0; j < GRID_SIZE; j++) {
      for (let i = 0; i < GRID_SIZE - 2; i++) {
        if (matchedPositions.has(`${i},${j}`)) continue;

        if (
          newBoard[i][j] !== "" &&
          newBoard[i][j] === newBoard[i + 1][j] &&
          newBoard[i][j] === newBoard[i + 2][j]
        ) {
          let matchLength = 3;
          while (
            i + matchLength < GRID_SIZE &&
            newBoard[i][j] === newBoard[i + matchLength][j]
          ) {
            matchLength++;
          }

          // Only award points if the game has started
          if (gameStarted) {
            // Calculate points based on match length with bonuses
            let points = matchLength * 10;

            // Add bonus points for longer matches
            if (matchLength === 4) {
              points += 20; // Bonus for 4 in a row
            } else if (matchLength >= 5) {
              points += 50; // Bonus for 5+ in a row
            }

            // Add floating points at the middle of the match
            const middleRow = i + Math.floor(matchLength / 2);
            addFloatingPoints(middleRow, j, points);

            // Immediately vibrate on match found before clearing
            vibrateDevice();
            playMatchSound();

            setScore((prev) => prev + points);
          }

          // Clear matched candies and track their positions
          for (let k = 0; k < matchLength; k++) {
            matchedPositions.add(`${i + k},${j}`);
            newBoard[i + k][j] = "";
          }

          matchFound = true;
          i += matchLength - 1;
        }
      }
    }

    if (matchFound) {
      // Drop candies down
      for (let j = 0; j < GRID_SIZE; j++) {
        // Start from the bottom row and move up
        for (let i = GRID_SIZE - 1; i >= 0; i--) {
          if (newBoard[i][j] === "") {
            // Find the closest non-empty cell above
            let foundNonEmpty = false;
            for (let k = i - 1; k >= 0; k--) {
              if (newBoard[k][j] !== "") {
                newBoard[i][j] = newBoard[k][j];
                newBoard[k][j] = "";
                foundNonEmpty = true;
                break;
              }
            }

            // If no non-empty cells above, don't need to check further for this column
            if (!foundNonEmpty) {
              break;
            }
          }
        }
      }

      // Fill empty spaces with new candies
      for (let i = 0; i < GRID_SIZE; i++) {
        for (let j = 0; j < GRID_SIZE; j++) {
          if (newBoard[i][j] === "") {
            newBoard[i][j] =
              CANDY_IMAGES[Math.floor(Math.random() * CANDY_IMAGES.length)];
          }
        }
      }

      // Only update the board state if matches were found and changes were made
      setBoard(newBoard);

      // Return true to indicate that matches were found
      return true;
    }

    // Return false if no matches were found
    return false;
  };

  const startGame = () => {
    // Generate a new board only if there's none or if game is over
    if (board.length === 0 || gameOver) {
      initializeBoard();
    }

    // Make sure to call these in this specific order
    setGameOver(false);
    setIsGameOver(false);
    setMessage("");
    setFloatingPoints([]);
    setScore(0);
    setMoves(25);
    setGameActive(true);
    setGameStarted(false);
    playClick();

    console.log("Start game called - gameActive:", true);
  };

  // Handle context menu prevention
  const preventContextMenu = (e) => {
    e.preventDefault();
    return false;
  };

  // Prevent app minimization for the entire page
  useEffect(() => {
    // This block is replaced with the more comprehensive solution above
    // So we can remove this useEffect completely
  }, []);

  useEffect(() => {
    logPageView("memecrush ", "memecrush");
  }, []);

  // Add CSS for the pulse animation to the head
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      @keyframes pulseBorder {
        0% {
          box-shadow: 0 0 0 0 rgba(255, 215, 0, 0.7);
        }
        70% {
          box-shadow: 0 0 0 10px rgba(255, 215, 0, 0);
        }
        100% {
          box-shadow: 0 0 0 0 rgba(255, 215, 0, 0);
        }
      }
      
      .hint-pulse {
        animation: pulseBorder 1.5s infinite;
        z-index: 5;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <>
      <div
        className="menupointer game-container4"
        style={{
          height: "100%",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          pointerEvents: "fill",
          touchAction: "none", // Disable browser handling of all touch gestures
        }}
        onContextMenu={preventContextMenu}
      >
        <img
          onClick={() => {
            toogleMenu(task, "task");
          }}
          src="https://d1ccopf5cw1hft.cloudfront.net/s3data/cancelicon.svg"
          className="cancel-full"
          style={{ cursor: "pointer" }}
        />
        <button
          className="btn-help-candy"
          onClick={() => setShowInstructionsModal(true)}
        >
          <i className="bi bi-question-circle"></i>
        </button>

        <div className="container">
          <div className="row justify-content-center">
            <div className="col-12 col-md-12 col-lg-12">
              <div className="game-container2">
                <div className="card-header text-center text-white position-relative">
                  <h2 className="mb-0 text-uppercase welcome-text">
                    Meme Crush
                  </h2>
                </div>
                <div className="game-content">
                  {!gameActive && !gameOver && (
                    <div className="text-center mb-3">
                      <button
                        className="start-btn mb-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          startGame();
                        }}
                      >
                        Start Game
                      </button>

                      {score > 0 && (
                        <div className="mt-2">
                          <p className="lead text-white text-uppercase">
                            Your previous score: <strong>{score}</strong>
                          </p>
                        </div>
                      )}

                      {initialMatchCount > 0 && (
                        <div className="mt-2">
                          <p
                            className="lead text-white text-uppercase"
                            style={{ color: "#FFD700" }}
                          >
                            <strong>
                              Possible matches: {initialMatchCount}
                            </strong>
                          </p>
                        </div>
                      )}

                      <div className="text-center mt-2">
                        <p className="mb-0 text-white instructions-text text-uppercase">
                          <strong>How to Play:</strong> swipe to connect
                          adjacent Memes. Match 3 or more in a row!
                          <br />
                        </p>
                        <span
                          className="mt-2 text-uppercase"
                          style={{ color: "#FFD700" }}
                        >
                          BONUS: +20 Tokens for 4 in a row, +50 Tokens for 5+ in
                          a row!
                        </span>
                      </div>
                    </div>
                  )}

                  {gameActive && (
                    <>
                      <div className="stats-row mb-3">
                        <div className="stat-item">
                          <span className="stat-label text-uppercase">
                            Score:
                          </span>
                          <span className="stat-value">{score}</span>
                        </div>
                        <div className="stat-item">
                          <span className="stat-label text-uppercase">
                            Moves left:
                          </span>
                          <span className="stat-value">{moves}</span>
                        </div>
                      </div>

                      {message && (
                        <div className="alert alert-warning text-center mb-3">
                          {message}
                        </div>
                      )}

                      <div
                        ref={boardRef}
                        className="candy-board"
                        style={{ touchAction: "none" }} // Add touchAction to the board specifically
                        onTouchStart={handleTouchStart}
                        onTouchMove={(e) => {
                          // Stop propagation to prevent the global handler from blocking this
                          e.stopPropagation();
                          handleTouchMove(e);
                        }}
                        onTouchEnd={handleTouchEnd}
                        onContextMenu={preventContextMenu}
                      >
                        {board.map((row, rowIndex) =>
                          row.map((candy, colIndex) => {
                            // Determine if this cell is being animated
                            const isAnimating = animatingCells.some(
                              (cell) =>
                                cell.row === rowIndex && cell.col === colIndex
                            );

                            // Determine animation class based on direction
                            let animationClass = "";
                            if (isAnimating && swapDirection) {
                              animationClass = `candy-swapping-${swapDirection}`;
                            }

                            return (
                              <div
                                key={`${rowIndex}-${colIndex}`}
                                onClick={() =>
                                  handleCandyClick(rowIndex, colIndex)
                                }
                                onContextMenu={preventContextMenu}
                                className={`candy-tile ${
                                  selectedCandy &&
                                  selectedCandy.row === rowIndex &&
                                  selectedCandy.col === colIndex
                                    ? "selected1"
                                    : ""
                                } ${animationClass} ${
                                  hintPositions.some(
                                    (pos) =>
                                      pos.row === rowIndex &&
                                      pos.col === colIndex
                                  )
                                    ? "hint-pulse"
                                    : ""
                                }`}
                              >
                                <img src={candy} alt={candy} />
                              </div>
                            );
                          })
                        )}

                        {/* Floating points animations */}
                        {floatingPoints.map((point) => (
                          <div
                            key={point.id}
                            className="floating-points"
                            style={{
                              top: `${(point.row * 100) / GRID_SIZE}%`,
                              left: `${(point.col * 100) / GRID_SIZE}%`,
                            }}
                          >
                            +{point.points}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Instructions Modal */}
              {showInstructionsModal && (
                <div className="instructions-modal-backdropmeme">
                  <div
                    className="instructions-modalmeme"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="instructions-modal-header">
                      <h3 className="rainbow-title mb-0 text-uppercase">
                        How to Play
                      </h3>
                      <button
                        className="btn-close btn-close-white"
                        onClick={() => setShowInstructionsModal(false)}
                        aria-label="Close"
                      ></button>
                    </div>

                    <div className="instructions-modal-body">
                      <div className="instruction-item">
                        <div className="instruction-icon">
                          <i className="bi bi-arrow-left-right"></i>
                        </div>
                        <div className="instruction-text">
                          <strong>How to Play:</strong> swipe to connect
                          adjacent memes. Match 3 or more in a row!
                          <br />
                        </div>
                      </div>

                      <div className="instruction-item">
                        <div className="instruction-icon">
                          <i className="bi bi-link-45deg"></i>
                        </div>
                        <div className="instruction-text">
                          {" "}
                          BONUS: +20 Tokens for 4 in a row, +50 Tokens for 5+ in
                          a row!
                        </div>
                      </div>

                      <div className="instruction-item">
                        <div className="instruction-icon">
                          <i className="bi bi-controller"></i>
                        </div>
                        <div className="instruction-text">
                          You have 25 moves to get the highest score
                        </div>
                      </div>
                    </div>

                    <div className="instructions-modal-footer">
                      <button
                        className="btn btn-primary px-4"
                        onClick={() => setShowInstructionsModal(false)}
                      >
                        Got it!
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {IsGameOver && (
                <div className="overlay">
                  <div className="quento-full-popup">
                    <div className="quento-popup-content">
                      <h2 className="quento-epic">Game Over!</h2>
                      <img
                        src="https://d1ccopf5cw1hft.cloudfront.net/s3data/Xicon.png"
                        className="quento-cancel-img"
                        onClick={() => {
                          playClick();
                          closePopUp();
                        }}
                      />
                      {!IsPointsReached ? (
                        <div className="row text-center">
                          <div className="col-12">
                            <div
                              className="quento-epic-div"
                              style={{ padding: "30px 40px" }}
                            >
                              <p className="quento-pointsLabel">
                                TOKENS EARNED
                              </p>
                              <div className="quento-pointsValue1">
                                <img
                                  src="https://d1ccopf5cw1hft.cloudfront.net/s3data/coinlogo.png"
                                  alt="logo"
                                />
                                <p className="quento-pointsNumber">{score}</p>
                              </div>
                            </div>
                            <p className="quento-bottom-text">
                              Keep matching candies! 🍭 Challenge yourself to
                              beat your high score!
                            </p>
                            <button
                              className="control-btn"
                              onClick={() => {
                                playClick();
                                setScore(0);
                                setGameOver(false);
                                setIsGameOver(false);
                                setGameActive(true);
                                setMoves(25);
                                startGame();
                              }}
                            >
                              Play Again
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="row text-center">
                          <div className="col-12">
                            <div className="quento-epic-div">
                              <p
                                className="quento-pointsLabel"
                                style={{
                                  fontSize: "18px",
                                  fontWeight: "bold",
                                  margin: "20px 0",
                                  textAlign: "center",
                                }}
                              >
                                POINTS EARNED: {score} <br />
                                TOKENS EARNED: NIL
                              </p>
                            </div>
                            <p className="quento-bottom-text text-uppercase">
                              Keep matching candies! 🍭 Challenge yourself to
                              beat your high score!
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
